import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@material-ui/core';
import {
  EntityCatalogGraphCard,
  EntityNode,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_CHILD_OF,
  RELATION_PARENT_OF,
} from '@backstage/catalog-model';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import {
  INHERITS_WORKS_ON_BEHALF_OF,
  INHERITS_WORKS_ON_BEHALF_OF_BY,
  WORKS_ON_BEHALF_OF,
  WORKS_ON_BEHALF_OF_BY,
} from '@internal/backstage-catalog-utils';
import { entityRefToCatalogPath } from '../shared/entityRefs';

/**
 * The only relations this card is meant to visualise: the Group↔Group
 * reporting hierarchy plus the worksOnBehalfOf and inheritsWorksOnBehalfOf
 * pairs described in the legend below.
 *
 * This whitelist has to be passed explicitly. Without a `relations` prop the
 * card falls back to the catalog-graph api's default relation set, which
 * packages/app/src/apis.ts widens to *every* known relation
 * (`defaultRelationTypes: { exclude: [] }`). That pulled ownedBy/ownerOf and
 * hasMember/memberOf into the graph, so owned Components/APIs/Resources and
 * User members were rendered as clickable nodes the legend never described —
 * and clicking one crashed the page with parseEntityRef's "missing or empty
 * kind". Ownership and membership for a team are already covered by
 * EntityOwnershipCard and EntityTeamMembersCard on the same group page.
 *
 * Mirrors the whitelist systemPage's /diagram route uses in
 * packages/app/src/components/catalog/EntityPage.tsx.
 */
export const TEAM_GRAPH_RELATIONS = [
  RELATION_PARENT_OF,
  RELATION_CHILD_OF,
  WORKS_ON_BEHALF_OF,
  WORKS_ON_BEHALF_OF_BY,
  INHERITS_WORKS_ON_BEHALF_OF,
  INHERITS_WORKS_ON_BEHALF_OF_BY,
];

/**
 * Wraps EntityCatalogGraphCard for the Group entity page with a legend
 * explaining the worksOnBehalfOf relations (PAT-677/PAT-679/PAT-1066).
 *
 * Deliberately does NOT pass a custom renderEdge to style the
 * worksOnBehalfOf edge differently: the Catalog Graph plugin's renderEdge
 * prop fully replaces default edge rendering for every edge (not just the
 * one being customized) — there's no public "default Edge" component to
 * fall back to for the other relations, so overriding it risks a fragile
 * reimplementation of arrowheads/curve rendering for no visual gain on the
 * 90% of edges that aren't ours. The AC this satisfies allows "styling OR
 * legend" — a legend caption is the lower-risk choice. Edges are still told
 * apart by label: the default label prints each edge's relation names.
 */
export function EntityTeamRelationsGraphCard() {
  const navigate = useNavigate();
  const errorApi = useApi(errorApiRef);

  // Backstop for the node-click crash. Passing onNodeClick fully replaces the
  // library's defaultOnNodeClick, which calls `parseEntityRef(node.id)` with no
  // defaults (CatalogGraphCard.esm.js:56) and therefore throws — taking the
  // whole page down via the error boundary — for any node id that isn't fully
  // qualified. We parse with explicit defaults and report instead of throwing,
  // so a malformed ref can degrade to a no-op no matter which upstream path
  // produced it.
  //
  // Trade-off: this also drops upstream's
  // `analytics.captureEvent('click', …)` on graph-node clicks for this one
  // card. Accepted deliberately — a user-visible crash outweighs analytics on
  // one of several navigation entry points. Considered and rejected for now:
  // re-adding it via `useAnalytics()` + a matching captureEvent call.
  const handleNodeClick = useCallback(
    (node: EntityNode) => {
      try {
        // defaultKind 'group': with TEAM_GRAPH_RELATIONS every node in this
        // graph is a Group, so a kind-less ref is a group by construction.
        // entityRefToCatalogPath hardcodes the catalog path shape instead of resolving through the app's route-binding system, matching this plugin's existing convention.
        navigate(
          entityRefToCatalogPath(node.id, {
            defaultKind: 'group',
            defaultNamespace: 'default',
          }),
        );
      } catch (error) {
        errorApi.post(
          new Error(
            `Could not open the catalog page for graph node "${node.id}"`,
            { cause: error },
          ),
        );
      }
    },
    [navigate, errorApi],
  );

  return (
    <Box>
      <EntityCatalogGraphCard
        height={400}
        relations={TEAM_GRAPH_RELATIONS}
        onNodeClick={handleNodeClick}
      />
      <Box mt={1}>
        <Typography variant="caption" color="textSecondary" component="div">
          <strong>Hierarchy</strong>: parent/child reporting lines.
        </Typography>
        <Typography variant="caption" color="textSecondary" component="div">
          <strong>worksOnBehalfOf</strong>: set explicitly on this group.
        </Typography>
        <Typography variant="caption" color="textSecondary" component="div">
          <strong>inheritsWorksOnBehalfOf</strong>: inherited from the nearest
          ancestor that sets one.
        </Typography>
      </Box>
    </Box>
  );
}
